import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../rules/rules.css";
import "./lobby.css";
import { selectAdminToken, selectSessionId, selectOrganizationId } from "../../sessionSlice";
import { getSessionPlayers } from "../../functions/getSessionPlayers";
import {
  getSpaceGamePlayersFromFirebase,
  getSpaceGameSessionStatus,
  startSpaceGameSession,
  pauseSpaceGameSession,
  getSpaceGameLeaderboardVisibility,
  showSpaceGameLeaderboard,
  hideSpaceGameLeaderboard,
  getSpaceGameDayNightMode,
  setSpaceGameDayMode,
  setSpaceGameNightMode,
} from "../../../functions/spaceGameFirebase";

const REFRESH_INTERVAL_MS = 6000;

function formatSeconds(totalSeconds) {
  const n = Math.max(0, Number(totalSeconds) || 0);
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const AdminLobby = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);

  const [players, setPlayers] = useState([]);
  const [firebasePlayers, setFirebasePlayers] = useState({});
  const [sessionState, setSessionState] = useState(null);
  const [leaderboardVisible, setLeaderboardVisible] = useState(false);
  const [dayNightMode, setDayNightMode] = useState("day");
  const [toggling, setToggling] = useState(false);
  const [togglingLeaderboard, setTogglingLeaderboard] = useState(false);
  const [togglingDayNight, setTogglingDayNight] = useState(false);

  const loadPlayers = useCallback(async () => {
    if (!adminToken) return;
    try {
      const data = await getSessionPlayers({ token: adminToken });
      setPlayers(data.players || []);
    } catch (e) {
      console.error("Failed to load session players:", e);
    }
  }, [adminToken]);

  // Points/time come from Firebase — Unity is the live source of truth for those two
  // fields. Backend still drives the player list itself (names/joined status).
  const loadFirebaseData = useCallback(async () => {
    if (!sessionId || !organizationId) return;
    try {
      const [fbPlayers, status, leaderboard, dayNight] = await Promise.all([
        getSpaceGamePlayersFromFirebase({ sessionId, organizationId }),
        getSpaceGameSessionStatus({ sessionId, organizationId }),
        getSpaceGameLeaderboardVisibility({ sessionId, organizationId }),
        getSpaceGameDayNightMode({ sessionId, organizationId }),
      ]);
      setFirebasePlayers(fbPlayers || {});
      setSessionState(status?.state || null);
      setLeaderboardVisible(Boolean(leaderboard?.visible));
      setDayNightMode(dayNight?.mode === "night" ? "night" : "day");
      console.log("[AdminLobby] sessionId/organizationId:", sessionId, organizationId);
      console.log("[AdminLobby] resolved sessionState:", status?.state || null);
      console.log("[AdminLobby] resolved leaderboardVisible:", Boolean(leaderboard?.visible));
      console.log("[AdminLobby] resolved dayNightMode:", dayNight?.mode === "night" ? "night" : "day");
    } catch (e) {
      console.error("Failed to load Firebase session data:", e);
    }
  }, [sessionId, organizationId]);

  useEffect(() => {
    loadPlayers();
    loadFirebaseData();
  }, [loadPlayers, loadFirebaseData]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadPlayers();
      loadFirebaseData();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadPlayers, loadFirebaseData]);

  const mergedPlayers = useMemo(
    () =>
      players.map((p) => {
        const fb = firebasePlayers[p.userId];
        return {
          ...p,
          points: fb?.totalPoints ?? p.points ?? 0,
          time: fb?.totalTime ?? 0,
        };
      }),
    [players, firebasePlayers]
  );

  const handleStart = async () => {
    setToggling(true);
    try {
      await startSpaceGameSession({ sessionId, organizationId });
      setSessionState("started");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to start game.", "error");
    } finally {
      setToggling(false);
    }
  };

  const handlePause = async () => {
    setToggling(true);
    try {
      await pauseSpaceGameSession({ sessionId, organizationId });
      setSessionState("paused");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to pause game.", "error");
    } finally {
      setToggling(false);
    }
  };

  const handleShowLeaderboard = async () => {
    setTogglingLeaderboard(true);
    try {
      await showSpaceGameLeaderboard({ sessionId, organizationId });
      setLeaderboardVisible(true);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to show leaderboard.", "error");
    } finally {
      setTogglingLeaderboard(false);
    }
  };

  const handleHideLeaderboard = async () => {
    setTogglingLeaderboard(true);
    try {
      await hideSpaceGameLeaderboard({ sessionId, organizationId });
      setLeaderboardVisible(false);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to hide leaderboard.", "error");
    } finally {
      setTogglingLeaderboard(false);
    }
  };

  const handleSetDayMode = async () => {
    setTogglingDayNight(true);
    try {
      await setSpaceGameDayMode({ sessionId, organizationId });
      setDayNightMode("day");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to set day mode.", "error");
    } finally {
      setTogglingDayNight(false);
    }
  };

  const handleSetNightMode = async () => {
    setTogglingDayNight(true);
    try {
      await setSpaceGameNightMode({ sessionId, organizationId });
      setDayNightMode("night");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to set night mode.", "error");
    } finally {
      setTogglingDayNight(false);
    }
  };

  return (
    <div className="rules-page-layout">
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder lobby-scope">
          <div className="admin-tab-container lobby-players-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-user-group"></i> &nbsp; Players Joined ({mergedPlayers.length})
            </div>
            {mergedPlayers.length === 0 ? (
              <p className="lobby-empty">No players have joined yet.</p>
            ) : (
              <div className="lobby-table-wrap">
                <table className="lobby-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Points</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedPlayers.map((p) => (
                      <tr key={p.userId}>
                        <td>{p.name || p.userId}</td>
                        <td>
                          <span
                            className={`lobby-badge ${
                              p.status === "completed" ? "lobby-badge--done" : "lobby-badge--joined"
                            }`}
                          >
                            {p.status === "completed" ? "Completed" : "Joined"}
                          </span>
                        </td>
                        <td>{p.points}</td>
                        <td>{formatSeconds(p.time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="admin-tab-container lobby-actions-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-gamepad"></i> &nbsp; Game Controls
            </div>
            <div className="lobby-actions-list">
              <button
                type="button"
                className="lobby-btn lobby-btn--outline"
                onClick={() => navigate("/admin/spacegame-settings")}
              >
                Game Settings
              </button>
              <button
                type="button"
                className="lobby-btn lobby-btn--outline"
                onClick={() => navigate("/admin/spacegame-leaderboard")}
              >
                Leaderboard
              </button>
            </div>

            <div className="lobby-controls-section">
              <div className="lobby-controls-title">Controls</div>
              <div className="lobby-actions-list">
                {sessionState === "started" ? (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--pause"
                    onClick={handlePause}
                    disabled={toggling}
                  >
                    {toggling ? "Pausing..." : "Pause"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--start"
                    onClick={handleStart}
                    disabled={toggling}
                  >
                    {toggling ? "Starting..." : "Start"}
                  </button>
                )}
                {leaderboardVisible ? (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--pause"
                    onClick={handleHideLeaderboard}
                    disabled={togglingLeaderboard}
                  >
                    {togglingLeaderboard ? "Hiding..." : "Hide Leaderboard"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--start"
                    onClick={handleShowLeaderboard}
                    disabled={togglingLeaderboard}
                  >
                    {togglingLeaderboard ? "Showing..." : "Show Leaderboard"}
                  </button>
                )}
              </div>
            </div>

            <div className="lobby-controls-section">
              <div className="lobby-controls-title">Other Controls</div>
              <div className="lobby-actions-list">
                {dayNightMode === "night" ? (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--day"
                    onClick={handleSetDayMode}
                    disabled={togglingDayNight}
                  >
                    {togglingDayNight ? "Switching..." : "Day Mode"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="lobby-btn lobby-btn--night"
                    onClick={handleSetNightMode}
                    disabled={togglingDayNight}
                  >
                    {togglingDayNight ? "Switching..." : "Night Mode"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLobby;
